/**
 * Connection machine — types.
 *
 * The assistant's WebSocket transport is modeled as a pure state machine:
 * `reducer.ts` is a pure `(state, event) → { state, effects }` function and
 * `useAssistantConnection` is a thin driver that owns the real socket/timers
 * and executes the effects the reducer requests.
 *
 * Why a machine: both GP2026 P0 bugs (#96 typed-prompt swallowed, #79 new-chat
 * dead window) came from transient socket state leaking into product decisions.
 * Here the socket is a transport detail; user intent (outbound messages, session
 * switches) is durable state the reducer honors regardless of socket phase.
 *
 * INVARIANT: connection LIFECYCLE (open/close/error/backoff/session-change)
 * never writes UIState — only user intent and semantic server events do.
 *
 * Hand-rolled on purpose (no XState dependency): the shape mirrors XState's
 * `(state, event) → state + actions`, so migrating later is mechanical.
 */

// ── Connection phase ──────────────────────────────────────────────

/** Lifecycle phase of the browser↔backend socket (NOT upstream OpenAI —
 *  the server's own `reconnecting`/`reconnected` events are a display-only
 *  overlay and deliberately not modeled here). */
export const ConnectionPhase = {
  /** Before the first CONNECT_REQUESTED. */
  IDLE: 'idle',
  /** A socket is being opened (initial, retry, or session switch). */
  CONNECTING: 'connecting',
  /** Socket open; SETTINGS sent; sends go straight to the wire. */
  OPEN: 'open',
  /** Unexpected drop; a retry is scheduled (RECONNECT_DELAY_MS). */
  BACKOFF: 'backoff',
  /** Retry budget exhausted. Terminal until user intent revives it
   *  (a new SEND or RETRY_MESSAGE re-enters CONNECTING). */
  FAILED: 'failed',
} as const;
export type ConnectionPhase =
  (typeof ConnectionPhase)[keyof typeof ConnectionPhase];

// ── Outbox ────────────────────────────────────────────────────────

/** Delivery lifecycle of one outbound user message. */
/** Which wire frame an outbox item serializes to. Added for the live-agent
 *  handoff bridge (2026-07-hubspot-native-handoff.md): a handoff reply must
 *  become an `agent_text` frame (carrying handoffId) instead of Sara's plain
 *  `text` frame, while reusing the SAME outbox/ack/retry lifecycle — see
 *  send-routing.ts for the pure decision of which kind a given send is. */
export const OutboundFrameKind = {
  TEXT: 'text',
  AGENT_TEXT: 'agent_text',
} as const;
export type OutboundFrameKind =
  (typeof OutboundFrameKind)[keyof typeof OutboundFrameKind];

export const SendStatus = {
  /** Held in the outbox; no open socket yet. Shows "Sending…". */
  QUEUED: 'queued',
  /** Written to the wire; awaiting the server's `message_received` ack.
   *  Shows "Sending…" (clears fast — the ack round-trip is ~ms). */
  SENT: 'sent',
  /** Server confirmed receipt (or legacy no-ack backend assumed — see
   *  ACK_TIMEOUT in the reducer). No indicator. */
  ACKED: 'acked',
  /** Delivery failed (retry budget exhausted / ack timeout on an
   *  ack-capable backend). Shows "Not sent · Retry". */
  FAILED: 'failed',
} as const;
export type SendStatus = (typeof SendStatus)[keyof typeof SendStatus];

/** One in-flight outbound message. `id` is a client-minted uuid that is BOTH
 *  the React `Message.id` and the wire `metadata.client_msg_id` — one identity
 *  for rendering, ack correlation, and the backend's at-most-once dedup.
 *  The outbox holds only in-flight items (queued|sent); acked/failed items are
 *  pruned — their terminal status lives on the chat `Message`. */
export interface OutboxItem {
  id: string;
  text: string;
  /** Wire attempts so far (for observability; dedup makes re-sends safe). */
  attempts: number;
  status: typeof SendStatus.QUEUED | typeof SendStatus.SENT;
  /** Which wire frame to serialize this item as (defaults to TEXT — see the
   *  SEND case in reducer.ts). */
  frameKind: OutboundFrameKind;
  /** Present when frameKind === AGENT_TEXT — the handoff this reply belongs to. */
  handoffId?: string;
}

// ── Machine state ─────────────────────────────────────────────────

export interface ConnState {
  phase: ConnectionPhase;
  /** Consecutive unexpected-close retries scheduled since the last
   *  successful open. Mirrors the legacy hook's reconnectAttempts. */
  attempts: number;
  /** Set true by SESSION_CHANGED when a live socket must be torn down first;
   *  consumed by the resulting SOCKET_CLOSED to reconnect IMMEDIATELY
   *  (intentional switch) instead of entering recovery backoff. */
  pendingSwitch: boolean;
  /** Ack capability gate: null until the first `message_received` ack proves
   *  the backend acks (then true). Gates ONLY the ACK_TIMEOUT policy:
   *  true → a silent deadline fails the bubble fast (explicit Retry);
   *  null → honest waiting — the timeout is a no-op, items stay SENT until
   *  an ack resolves them or SOCKET_CLOSED re-queues them for the reconnect
   *  reflush. Invariant this leans on: every deployed backend acks
   *  client_msg_id frames, so "still SENT" is always a transient state. A
   *  hypothetical never-acking backend would leave the bubble at "Sending…"
   *  until the socket closes (then resend) — accepted residual, since no
   *  such backend exists in any environment. It does NOT gate the drop
   *  path: un-acked SENT items are always re-queued on SOCKET_CLOSED (see
   *  the reducer's tab-wake comment — the backend's manager-level
   *  client_msg_id dedup makes the resend at-most-once). */
  serverAcks: boolean | null;
  outbox: OutboxItem[];
}

export const INITIAL_STATE: ConnState = {
  phase: ConnectionPhase.IDLE,
  attempts: 0,
  pendingSwitch: false,
  serverAcks: null,
  outbox: [],
};

// ── Events (inputs) ───────────────────────────────────────────────

export type ConnEvent =
  /** Mount: open the first socket. */
  | { type: 'CONNECT_REQUESTED' }
  /** Driver: ws.onopen fired AND the SETTINGS frame was sent. */
  | { type: 'SOCKET_OPENED' }
  /** Driver: ws.onclose fired (any close — intentional or drop). */
  | { type: 'SOCKET_CLOSED' }
  /** Driver: the backoff timer elapsed. */
  | { type: 'RETRY_TICK' }
  /** User submitted a message (id pre-minted by the caller). `frameKind`/
   *  `handoffId` route a live-agent handoff reply as `agent_text` instead of
   *  Sara's `text` (see send-routing.ts); omitted → TEXT (Sara), unchanged
   *  from before the handoff feature. */
  | {
      type: 'SEND';
      id: string;
      text: string;
      frameKind?: OutboundFrameKind;
      handoffId?: string;
    }
  /** Server echoed `message_received` for this client_msg_id. */
  | { type: 'ACK_RECEIVED'; id: string }
  /** The ack-timeout timer for a send batch elapsed. */
  | { type: 'ACK_TIMEOUT' }
  /** User clicked "Retry" on a failed bubble (text re-supplied by the
   *  caller from the chat message — failed items are pruned from the outbox).
   *  Same frameKind/handoffId passthrough as SEND. */
  | {
      type: 'RETRY_MESSAGE';
      id: string;
      text: string;
      frameKind?: OutboundFrameKind;
      handoffId?: string;
    }
  /** New chat or resume: tear down and reconnect under the new session id.
   *  The outbox is cleared — a message typed against conversation A must
   *  never flush into conversation B. */
  | { type: 'SESSION_CHANGED' };

// ── Effects (outputs the driver executes) ─────────────────────────

export type ConnEffect =
  /** Create a WebSocket for the CURRENT sessionId and attach handlers. */
  | { type: 'OPEN_SOCKET' }
  /** Close the current socket (code 1000). */
  | { type: 'CLOSE_SOCKET'; reason: string }
  /** Serialize + send these outbox items as `text` frames (socket is open). */
  | { type: 'SEND_FRAMES'; items: OutboxItem[] }
  /** Arm the recovery backoff timer → dispatch RETRY_TICK. */
  | { type: 'SCHEDULE_RETRY'; delayMs: number }
  /** Arm the ack timer → dispatch ACK_TIMEOUT. Idempotent to fire with an
   *  empty in-flight set, so overlapping timers are harmless. */
  | { type: 'SCHEDULE_ACK_TIMEOUT'; delayMs: number }
  /** Reconnect budget exhausted → surface the terminal "lost connection"
   *  feedback (toast), exactly like the legacy hook. */
  | { type: 'NOTIFY_TERMINAL' }
  /** Sync delivery statuses onto the chat Messages (by id). */
  | {
      type: 'UPDATE_MESSAGE_STATUS';
      updates: { id: string; status: SendStatus }[];
    };

export interface ReducerResult {
  state: ConnState;
  effects: ConnEffect[];
}

// ── Policy constants (single source of truth for transport policy) ─

/** Delay between recovery retries after an unexpected drop (legacy parity). */
export const RECONNECT_DELAY_MS = 3000;
/** Recovery retries before giving up (legacy parity). */
export const MAX_RECONNECT_ATTEMPTS = 5;
/** How long a SENT item may wait for its ack before the timeout resolves it. */
export const ACK_TIMEOUT_MS = 5000;
